import React, {FC, useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage, TopProgress } from "../../../components";
import Illustration from './illustration.svg'
import IllustrationDark from './illustration-dark.svg'
import { useNavigate } from "react-router";
import { sshService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import {timeConverterMMDDYYHHMM} from '../../../utils/dates'
import { useAuth } from "../../../context/user.context";
import rootConfig from "../../../config";
import { compareArrays } from "../../../utils/findIntersection";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import useUpdateEffect from "../../../hooks/useUpdateEffect";

const SSH_FILTER_KEYS = ['created_at', 'user'];
const DEFAULT_ORDERING = '-created_at';

const SSHKeys:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {
    viewAlert,
    setViewModalComplete,
    setActiveAnimationComplete,
  } = useUi()
  const {user} = useAuth()
  const [keys, setKeys] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [deletedKey, setSeletedKey] = useState<any>()
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [paginationData, setPaginationData] = useState<any>()
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return SSH_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);
  const filterBarOptions = normalizedFilterOptions || filterOptions;
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(keys, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const updateKeys = () => {
    setLoading(true)
    sshService.getSshKeys({
      page:page||1,
      pageSize:pageSize||10,
      ordering:ordering || DEFAULT_ORDERING,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res) => {
      // Фолбек для фильтров, если API не вернул filter_options
      const apiFilters = res?.data?.filter_options;
      if (apiFilters) {
        setFilterOptions(apiFilters);
      } else {
        const users = Array.from(
          new Map(
            res.data.objects
              .filter((item:any) => item.user)
              .map((item:any) => [item.user.id, { display: item.user.display || item.user.email || item.user.id, value: item.user.id }])
          ).values()
        );
        setFilterOptions(users.length ? { user: users } : {});
      }
      setPaginationData(res.data)
      setKeys(res.data.objects.map((key:any) => {
        return {
          ...key,
          name:key.name,
          fingerPrint:key.fingerprint,
          created_at: timeConverterMMDDYYHHMM(key.created_at),
          id:key.id
        }
      }))
    })
    .catch((e) => {
      if(e?.response?.data.detail){
        viewAlert({
          severity:'error',
          message:e.response.data.detail,
        })
      }
    })
    .finally(() => {
      setPageLoading(false)
      setLoading(false)
    })
  }
  useEffect(() => {
    updateKeys()
    const interval = setInterval(() => {
      updateKeys()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  },[page, ordering, pageSize, searchString, filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  const items = [
    {
      title:t('EDIT'),
      id:'2',
      icon:icons.Edit,
      onClick: (id:any) => navigate(`edit/${id.id}`),
      divider:false
    },
    {
      title:t('DELETE'),
      id:'4',
      onClick:(id:any) => {
        openModal('delete')
        setAlertTitle(t("SSH_KEY"))
        setAlertSubTitle([id])
        setSeletedKey(id)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  const _deleteSsh = async () => {
    setPageLoading(true)
    if(allChecked) {
      let promises:any[] = []
      await keys.map(async(item) => {
        let promise = sshService.deleteSsh(item.id)
        promises.push(promise)
      })
      await Promise.all(promises)
      .then((res) => {
        updateKeys()
        setAllChecked(false)
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
    } else if (checked?.length > 0){
      let promises:any[] = []
      await checked.map(async(item) => {
        let promise = sshService.deleteSsh(item.id)
        promises.push(promise)
      })
      await Promise.all(promises)
      .then((res) => {
        updateKeys()
        setChecked([])
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
    } else if (deletedKey) {
      sshService.deleteSsh(deletedKey.id)
      .then((res) => {
        updateKeys()
      })
      .catch((e) => {
        if(e?.response?.data.detail){
          viewAlert({
            severity:'error',
            message:e.response.data.detail,
          })
        }
      })
    }
  }
  const closeModal = () => {
    _deleteSsh()
    setViewModal(false)
    setActiveAnimation(false)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }
  const Columns = [
    {
      title:t("NAME"),
      key:'name',
      sorted:true
    },
    {
      title:t("FINGERPRINT"),
      key:'fingerPrint',
      sorted:false
    },
    {
      title:t("CREATED_AT"),
      key:'created_at',
      sorted:true
    },
    {
      title:'',
      key:'props',
      sorted:false
    },
  ]
  const _createFunc = () => {
    navigate('create')
  }
  
  return (
    <>
      <TopProgress loading={loading}/>
      <StartPage
        title={`${t("SSH_KEYS")}`}
        noResourceText={`${t("YOU_HAVE_NO_SSH")}`}
        createResourceButton={`${t('CREATE_SSH')}`}
        createResourceButtonEventOrLink={_createFunc}
        howCreateVideoUrl="https://www.youtube.com/embed/n0mEex_ri9U"
        haveResource={keys?.length > 0}
        rightButtonComponentCustom={
          <Button
            onClick={() => navigate('import')}
            title={t("IMPORT")}
            variant='stroke'
            size='display'
            icon={icons.Upload}
            // disabled
            customStyles={{margin:'0px 6px 0px 10px'}}
          >
            <img src={icons.DeleteIcon}/>
          </Button>
        }
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_SSH')}
                onClick={_createFunc}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <Button
                onClick={() => navigate('import')}
                title={t("IMPORT")}
                variant='stroke'
                size='display'
                icon={icons.Upload}
                customStyles={{margin:'0px 0px 0px 0px'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  onClick={() => {
                    setAlertTitle(t("SSH_KEYS"))
                    setAlertSubTitle(allChecked ? keys : checked)
                    openModal('delete')
                  }}
                  variant='icon'
                  size='display'
                  test_id='button-delete'
                  disabled={!allChecked && checked?.length === 0 }
                >
                  <img src={icons.DeleteIcon}/>
                </Button>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {filterBarOptions ? (
              <FilterBar filterOptions={filterBarOptions} />
            ) : null}
          </div>
        }
        learnMoreAboutResource={`${t('LEARN_MORE_SSH')}`}
        howCreateVideoText={`${t('HOW_CREATE_SSH')}`}
        learnMoreAboutResourceLink={`${rootConfig.docs_url}category/ssh-keys`}
        videoImage={Illustration}
        videoImageDark={IllustrationDark}
        pageLoading={pageLoading}
        tableData={{
          columns:Columns,
          data:keys,
          props:items,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          paginationData:paginationData,
          updateData:updateKeys
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
      />
    </>
  )
}


export default SSHKeys
